import { useCallback } from 'react';
import { useSnackbar } from '../../../../store/snackbar';
import { DropResult } from 'react-beautiful-dnd';
import { useIntl } from 'react-intl';
import { graphql } from '../../../../gql';
import { useApiMutation } from '../../../../query/hooks';
import { Phase } from '../../JourneyDetailPage';

const updateTouchpointPhase = graphql(`
	mutation timeline_updatePhasesTouchpoints($destinationPhase: UUID!, $touchpointId: UUID!, $order: Int!) {
		updateTouchpoint(by: { id: $touchpointId }, data: { phase: { connect: { id: $destinationPhase } }, order: $order }) {
			ok
			errorMessage
		}
	}
`);

type Props = Readonly<{
	state: readonly Phase[];
	setState: React.Dispatch<React.SetStateAction<readonly Phase[]>>;
}>;

export const useHandleChangeTouchpointsPhaseDetail = ({ state, setState }: Props): ((result: DropResult) => Promise<void>) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();
	const { mutateAsync: updatePhaseTouchpoints } = useApiMutation(updateTouchpointPhase, {
		onSuccess: (result) => {
			if (result.updateTouchpoint.errorMessage) {
				console.error(result.updateTouchpoint.errorMessage);
			}
		},
	});

	return useCallback(
		async (result: DropResult) => {
			const { source, destination, draggableId } = result;

			if (destination == null) {
				openSnackbar({
					open: true,
					message: intl.formatMessage({ id: 'timeline.phase.update.error' }),
					variant: 'alert',
					alert: {
						color: 'error',
					},
					close: true,
				});
				return;
			}

			setState((prev) => {
				const newState = [...prev];

				// get source phase and its touchpoints
				const sourcePhaseIndex = newState.findIndex(({ id }) => source.droppableId === id);
				const sourcePhaseTouchpoints = newState[sourcePhaseIndex].touchpoints;

				// get destination phase and its touchpoints
				const destinationPhaseIndex = newState.findIndex(({ id }) => destination.droppableId === id);
				const destinationPhaseTouchpoints = newState[destinationPhaseIndex].touchpoints;

				// find desired touchpoint containing dragged activity
				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
				if (sourcePhaseTouchpoints) {
					const touchpoint = sourcePhaseTouchpoints[source.index];

					// remove touchpoint with dragged activity from prior phase
					sourcePhaseTouchpoints.splice(source.index, 1);
					newState[sourcePhaseIndex].touchpoints = sourcePhaseTouchpoints;

					// add touchpoint to a phase where it got dropped
					destinationPhaseTouchpoints.splice(destination.index, 0, touchpoint);
					newState[destinationPhaseIndex].touchpoints = destinationPhaseTouchpoints;
				}

				return newState;
			});

			await updatePhaseTouchpoints({ destinationPhase: destination.droppableId, touchpointId: draggableId, order: destination.index });
		},
		[intl, setState, updatePhaseTouchpoints, openSnackbar],
	);
};
